import { createReducer } from '@reduxjs/toolkit';
import { OrderDataDto, OrderDto } from '../../api/ClientApi';

import {
    saveOrder,
    importOrders,
    addOrderGoodsItemToOrder,
    removeOrderGoodsItemFromOrder,
    addOrUpdateOrder,
    cancelOrder,
    saveReceptionOrderFromPurchaseOrder,
    removeOrderFromRedux,
    acceptOrder,
} from '../actions/orderActions';

export interface OrdersState {
    orders: OrderDto[] | null;
    ordersInfoDate: Date | null;
    ordersFetchActive: boolean;
}

export const ordersReducer = createReducer<OrdersState>(
    { orders: null, ordersInfoDate: null, ordersFetchActive: false },
    (builder) => {
        builder
            .addCase(addOrderGoodsItemToOrder, (state: OrdersState, action: any) => {
                const { orderId, orderGoodsItem } = action.payload;

                if (!orderGoodsItem || !state.orders) {
                    return;
                }

                const orderIndex = state.orders?.findIndex((o) => o.id === orderId);
                const orderGoodsItemId = orderGoodsItem.id;

                if (orderIndex !== undefined && orderIndex >= 0) {
                    const oldOrder = state.orders[orderIndex];

                    const oldOrderGoodsItemIndex = oldOrder.orderGoodsItems?.findIndex(
                        (ogi) => ogi.id === orderGoodsItemId,
                    );

                    if (oldOrderGoodsItemIndex !== undefined && oldOrderGoodsItemIndex >= 0) {
                        oldOrder.orderGoodsItems?.splice(oldOrderGoodsItemIndex, 1, orderGoodsItem);
                    } else {
                        if (!oldOrder.orderGoodsItems) {
                            oldOrder.orderGoodsItems = [orderGoodsItem];
                        } else {
                            oldOrder.orderGoodsItems.unshift(orderGoodsItem);
                        }
                    }

                    state.orders.splice(orderIndex, 1, oldOrder);
                }
            })
            .addCase(removeOrderGoodsItemFromOrder, (state: OrdersState, action: any) => {
                const { orderId, orderGoodsItemId } = action.payload;

                if (!orderGoodsItemId || orderGoodsItemId <= 0 || !state.orders) {
                    return;
                }

                const orderIndex = state.orders?.findIndex((o) => o.id === orderId);

                if (orderIndex !== undefined && orderIndex >= 0) {
                    const oldOrder = state.orders[orderIndex];

                    const oldOrderGoodsItemIndex = oldOrder.orderGoodsItems?.findIndex(
                        (ogi) => ogi.id === orderGoodsItemId,
                    );

                    if (oldOrderGoodsItemIndex !== undefined && oldOrderGoodsItemIndex >= 0) {
                        oldOrder.orderGoodsItems?.splice(oldOrderGoodsItemIndex, 1);

                        state.orders.splice(orderIndex, 1, oldOrder);
                    }
                }
            })
            .addCase(saveOrder.fulfilled, (state: OrdersState, action: any) => {
                if (state.orders === null) {
                    return;
                }

                const payload: OrderDto = action.payload;

                const order: OrderDto = new OrderDto();
                order.init(payload);

                const orderIndex = state.orders!.findIndex((a) => {
                    return a.id === order.id;
                });

                if (orderIndex >= 0) {
                    // update order
                    state.orders.splice(orderIndex, 1, order);
                } else {
                    // add order
                    state.orders.unshift(order);
                }
            })
            .addCase(saveReceptionOrderFromPurchaseOrder.fulfilled, (state: OrdersState, action: any) => {
                if (state.orders === null) {
                    return;
                }

                const payload: OrderDto = action.payload;

                const order: OrderDto = new OrderDto();
                order.init(payload);

                const orderIndex = state.orders!.findIndex((a) => {
                    return a.id === order.id;
                });

                if (orderIndex >= 0) {
                    // update order
                    state.orders.splice(orderIndex, 1, order);
                } else {
                    // add order
                    state.orders.unshift(order);
                }
            })
            .addCase(cancelOrder.fulfilled, (state: OrdersState, action: any) => {
                if (state.orders === null) {
                    return;
                }

                const payload: OrderDto = action.payload;

                const order: OrderDto = new OrderDto();
                order.init(payload);

                const orderIndex = state.orders!.findIndex((a) => {
                    return a.id === order.id;
                });

                if (orderIndex >= 0) {
                    // update order
                    state.orders.splice(orderIndex, 1, order);
                } else {
                    // add order
                    state.orders.unshift(order);
                }
            })
            .addCase(acceptOrder.fulfilled, (state: OrdersState, action: any) => {
                if (state.orders === null) {
                    return;
                }

                const payload: OrderDto = action.payload;

                const order: OrderDto = new OrderDto();
                order.init(payload);

                const orderIndex = state.orders!.findIndex((a) => {
                    return a.id === order.id;
                });

                if (orderIndex >= 0) {
                    // update order
                    state.orders.splice(orderIndex, 1, order);
                } else {
                    // add order
                    state.orders.unshift(order);
                }
            })
            .addCase(addOrUpdateOrder, (state: OrdersState, action: any) => {
                const newOrder = action.payload;

                const oldOrderIndex = state.orders?.findIndex((o) => o.id === newOrder.id);

                if (oldOrderIndex !== undefined && oldOrderIndex >= 0) {
                    state.orders?.splice(oldOrderIndex, 1, newOrder);
                } else {
                    if (!state.orders) {
                        state.orders = [newOrder];
                    } else {
                        state.orders.unshift(newOrder);
                    }
                }
            })
            .addCase(removeOrderFromRedux, (state: OrdersState, action: any) => {
                const newOrderId = action.payload;

                const oldOrderIndex = state.orders?.findIndex((o) => o.id === newOrderId);

                if (oldOrderIndex !== undefined && oldOrderIndex >= 0) {
                    state.orders?.splice(oldOrderIndex, 1);
                }
            })
            .addCase(importOrders.fulfilled, (state: OrdersState, action: any) => {
                const result: OrderDataDto = action.payload;

                const newOrders = result.orders;

                state.orders = newOrders?.sort((a, b) => (b.id ?? 0) - (a.id ?? 0)) ?? null;
                state.ordersInfoDate = result.infoDate ?? null;

                state.ordersFetchActive = false;
            })
            .addCase(importOrders.pending, (state: OrdersState, action: any) => {
                state.ordersFetchActive = true;
            })
            .addCase(importOrders.rejected, (state: OrdersState, action: any) => {
                state.ordersFetchActive = false;
            });
    },
);
